import { createReducer } from '@reduxjs/toolkit';
import type { ColorScheme, RadarFrame } from '../api.js';
import {
  weatherRadarClear,
  weatherRadarSetFrames,
  weatherRadarSetPlaying,
  weatherRadarSetTime,
} from './actions.js';

/**
 * The live state of the radar animation. Transient: it is what the server last
 * reported plus where the user is in it, and it is dropped when the layer goes.
 * The preferences that outlive a session live in `weatherRadarSettings`.
 */
export interface WeatherRadarState {
  frames: RadarFrame[];
  /** Colour schemes the server offers, named by it. */
  colorSchemes: ColorScheme[];
  /** When the server built the frame list; `null` before the first answer. */
  generated: number | null;
  /**
   * The frame the map shows, by its time. `null` follows the newest observed
   * frame, so a refresh moves the view forward on its own — which is what the
   * user who has not scrubbed anywhere expects to see.
   */
  selectedTime: number | null;
  playing: boolean;
}

export const weatherRadarInitialState: WeatherRadarState = {
  frames: [],
  colorSchemes: [],
  generated: null,
  selectedTime: null,
  playing: false,
};

/**
 * Keeps the forecast tail on the timeline across the gaps the server leaves in
 * it. LibreWXR publishes an **empty** nowcast while it regenerates one, every
 * few minutes, so a list read at the wrong moment has no forecast at all and
 * the tail would blink out of the UI. Frames held this way are still dropped
 * once the observed half catches up with them, and one that has gone for good
 * is handled where it shows: the layer keeps the frame it has and asks for a
 * fresh list.
 */
function withHeldForecast(
  previous: RadarFrame[],
  incoming: RadarFrame[],
): RadarFrame[] {
  if (incoming.some((frame) => frame.forecast)) {
    return incoming;
  }

  const newestObserved = incoming.reduce(
    (newest, frame) => Math.max(newest, frame.time),
    0,
  );

  const held = previous
    .filter((frame) => frame.forecast && frame.time > newestObserved)
    .map((frame) => ({ ...frame }));

  return held.length > 0 ? [...incoming, ...held] : incoming;
}

export const weatherRadarReducer = createReducer(
  weatherRadarInitialState,
  (builder) =>
    builder
      .addCase(
        weatherRadarSetFrames,
        (state, { payload: { frames, colorSchemes, generated } }) => {
          state.frames = withHeldForecast(state.frames, frames);

          state.colorSchemes = colorSchemes;

          state.generated = generated;

          // A frame the server has dropped can't be shown any more, so fall
          // back to following the newest one instead of showing nothing.
          if (
            state.selectedTime !== null &&
            !state.frames.some((frame) => frame.time === state.selectedTime)
          ) {
            state.selectedTime = null;
          }
        },
      )
      .addCase(weatherRadarSetTime, (state, { payload }) => {
        state.selectedTime = payload;
      })
      .addCase(weatherRadarSetPlaying, (state, { payload }) => {
        state.playing = payload;
      })
      .addCase(weatherRadarClear, () => weatherRadarInitialState),
);
